import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePlan } from '../context/PlanContext';

import {
  Calendar,
  CircleNotch,
  Eye,
  Funnel,
  Target,
  TrendUp,
  Trophy,
  UsersThree
} from '@phosphor-icons/react';
const ConsultantDashboard = () => {
  const navigate = useNavigate();
  const { userPlan, planLimits } = usePlan();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ 
    industry: 'all', 
    grade: 'all',
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    // Check if user has consultant/admin access
    if (!planLimits.is_consultant) {
      navigate('/');
      return;
    }

    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scanner/admin/scans`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 403) {
          navigate('/');
          return;
        }
        throw new Error('Failed to fetch scans');
      }

      const data = await response.json();
      setScans(data);
    } catch (error) {
      console.error('Error fetching scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade) => {
    const colors = {
      'A': 'text-[#14F195]',
      'B': 'text-[#6366F1]',
      'C': 'text-[#F59E0B]',
      'D': 'text-[#EF4444]',
      'F': 'text-[#DC2626]'
    };
    return colors[grade] || 'text-[#A0AEC0]';
  };

  const industries = ['all', ...new Set(scans.map(s => s.industry))];
  const grades = ['all', 'A', 'B', 'C', 'D'];

  // Get grade breakdown stats
  const gradeBreakdown = {
    A: scans.filter(s => s.readiness_grade === 'A').length,
    B: scans.filter(s => s.readiness_grade === 'B').length,
    C: scans.filter(s => s.readiness_grade === 'C').length,
    D: scans.filter(s => s.readiness_grade === 'D').length
  };

  // Get plan breakdown
  const planBreakdown = {
    starter: scans.filter(s => s.user_plan === 'starter' || !s.user_plan).length,
    pro: scans.filter(s => s.user_plan === 'pro').length,
    partner_pro: scans.filter(s => s.user_plan === 'partner_pro').length
  };

  const filteredScans = scans.filter(scan => {
    // Industry filter
    if (filter.industry !== 'all' && scan.industry !== filter.industry) {
      return false;
    }

    // Grade filter
    if (filter.grade !== 'all' && scan.readiness_grade !== filter.grade) {
      return false;
    }

    // Date range filter
    if (filter.dateFrom || filter.dateTo) {
      const scanDate = new Date(scan.created_at);
      if (filter.dateFrom && scanDate < new Date(filter.dateFrom)) {
        return false;
      }
      if (filter.dateTo && scanDate > new Date(filter.dateTo + 'T23:59:59')) {
        return false;
      }
    }

    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <CircleNotch size={48} className="text-[#6366F1] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26]">
      <Header />

      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">
                  Consultant <span className="gradient-text">Dashboard</span>
                </h1>
                <p className="text-[#A0AEC0]">
                  Manage and review all client scans
                </p>
              </div>
              
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] text-white">
                <UsersThree size={20} />
                <span className="font-semibold">Partner Pro</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">Total Scans</p>
                  <TrendUp size={20} className="text-[#14F195]" />
                </div>
                <p className="text-3xl font-bold text-white">{scans.length}</p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">Active Clients</p>
                  <UsersThree size={20} className="text-[#6366F1]" />
                </div>
                <p className="text-3xl font-bold text-white">{new Set(scans.map(s => s.user_email)).size}</p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">Total Companies</p>
                  <Target size={20} className="text-[#EC4899]" />
                </div>
                <p className="text-3xl font-bold text-white">
                  {new Set(scans.map(s => s.company_name)).size}
                </p>
              </div>

              <div className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[#A0AEC0] text-sm">This Month</p>
                  <Calendar size={20} className="text-[#F59E0B]" />
                </div>
                <p className="text-3xl font-bold text-white">
                  {scans.filter(s => {
                    const scanDate = new Date(s.created_at);
                    const now = new Date();
                    return scanDate.getMonth() === now.getMonth() && 
                           scanDate.getFullYear() === now.getFullYear();
                  }).length}
                </p>
              </div>
            </div>

            {/* Grade & Plan Breakdown */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Readiness Grade Breakdown */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Trophy size={20} className="text-[#6366F1]" />
                  <h3 className="text-lg font-semibold text-white">Readiness Grade Breakdown</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[#14F195] font-bold text-lg">A - AI Ready</span>
                    <span className="text-white font-semibold">{gradeBreakdown.A}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#6366F1] font-bold text-lg">B - Good Potential</span>
                    <span className="text-white font-semibold">{gradeBreakdown.B}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#F59E0B] font-bold text-lg">C - Getting Started</span>
                    <span className="text-white font-semibold">{gradeBreakdown.C}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#EF4444] font-bold text-lg">D - Foundation Building</span>
                    <span className="text-white font-semibold">{gradeBreakdown.D}</span>
                  </div>
                </div>
              </div>

              {/* Plan Breakdown */}
              <div className="glass rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <UsersThree size={20} className="text-[#EC4899]" />
                  <h3 className="text-lg font-semibold text-white">Plan Distribution</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[#A0AEC0] font-medium">Starter</span>
                    <span className="text-white font-semibold">{planBreakdown.starter}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#6366F1] font-medium">Pro</span>
                    <span className="text-white font-semibold">{planBreakdown.pro}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#EC4899] font-medium">Partner Pro</span>
                    <span className="text-white font-semibold">{planBreakdown.partner_pro}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Filters */}
          <div className="mb-6 glass rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <Funnel size={20} className="text-[#6366F1]" />
              <h3 className="text-lg font-semibold text-white">Filters</h3>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">Industry</label>
                <select
                  value={filter.industry}
                  onChange={(e) => setFilter({ ...filter, industry: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                >
                  <option value="all">All Industries</option>
                  {industries.filter(i => i !== 'all').map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">Readiness Grade</label>
                <select
                  value={filter.grade}
                  onChange={(e) => setFilter({ ...filter, grade: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                >
                  {grades.map(grade => (
                    <option key={grade} value={grade}>
                      {grade === 'all' ? 'All Grades' : `Grade ${grade}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">Date From</label>
                <input
                  type="date"
                  value={filter.dateFrom}
                  onChange={(e) => setFilter({ ...filter, dateFrom: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#A0AEC0] mb-2">Date To</label>
                <input
                  type="date"
                  value={filter.dateTo}
                  onChange={(e) => setFilter({ ...filter, dateTo: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl glass text-white bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                />
              </div>
            </div>
            
            {/* Clear Filters Button */}
            {(filter.industry !== 'all' || filter.grade !== 'all' || filter.dateFrom || filter.dateTo) && (
              <button
                onClick={() => setFilter({ industry: 'all', grade: 'all', dateFrom: '', dateTo: '' })}
                className="mt-4 px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white text-sm transition-all"
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Scans Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass rounded-3xl overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[rgba(255,255,255,0.05)]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Company</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Industry</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Client</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Plan</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Grade</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Opportunities</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScans.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-[#A0AEC0]">
                        No scans found
                      </td>
                    </tr>
                  ) : (
                    filteredScans.map((scan) => {
                      const planLabel = scan.user_plan === 'partner_pro' ? 'Partner Pro' : 
                                       scan.user_plan === 'pro' ? 'Pro' : 'Starter';
                      const planColor = scan.user_plan === 'partner_pro' ? 'text-[#EC4899]' :
                                       scan.user_plan === 'pro' ? 'text-[#6366F1]' : 'text-[#A0AEC0]';
                      
                      return (
                        <tr key={scan.id} className="border-t border-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                          <td className="px-6 py-4 text-white font-medium">{scan.company_name}</td>
                          <td className="px-6 py-4 text-[#A0AEC0]">{scan.industry}</td>
                          <td className="px-6 py-4 text-[#A0AEC0] text-sm">{scan.user_email || scan.user_name}</td>
                          <td className="px-6 py-4">
                            <span className={`text-sm font-semibold ${planColor}`}>
                              {planLabel}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`text-xl font-bold ${getGradeColor(scan.readiness_grade)}`}>
                              {scan.readiness_grade || '–'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-white">{scan.opportunity_count}</td>
                          <td className="px-6 py-4 text-[#A0AEC0] text-sm">
                            {new Date(scan.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => navigate(`/consultant/scans/${scan.id}`)}
                              className="flex items-center gap-2 px-4 py-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white text-sm transition-all"
                            >
                              <Eye size={16} />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConsultantDashboard;
